import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import {
  listRequests, decideRequest, deleteRequest, teamLogin, teamToken, teamLogout,
  TeamAuthError, PreviewRequestView,
} from '../../config/previewAccess';
import {
  LandingShell, Container, Narrow, Panel, Eyebrow, H2, Sub, StatusBadge, Field,
  ErrorText, PrimaryButton, GhostButton, T,
} from './ui';

const FiCheck = FiIcons.FiCheck as React.ComponentType<any>;
const FiX = FiIcons.FiX as React.ComponentType<any>;
const FiTrash = FiIcons.FiTrash2 as React.ComponentType<any>;

const appName = (id: string) => DEPLOYED_APPS.find(a => a.id === id)?.name ?? id;
const fmt = (s: string | null) => (s ? s.replace('T', ' ').slice(0, 16) : '—');

/**
 * Team review console. Requires an admin sign-in (reuses /api/auth/login → JWT);
 * the list/decision/delete endpoints are gated server-side by admin roles.
 */
const TeamRequests: React.FC = () => {
  const [authed, setAuthed] = useState<boolean>(() => !!teamToken());
  const [requests, setRequests] = useState<PreviewRequestView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<Record<string, string[]>>({});

  // team login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setRequests(await listRequests());
      setAuthed(true);
    } catch (e) {
      if (e instanceof TeamAuthError) { setAuthed(false); }
      else { setError('Could not load requests.'); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (teamToken()) load(); }, [load]);

  const onLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError(null); setLoggingIn(true);
    try {
      const ok = await teamLogin(email, password);
      if (!ok) { setLoginError('Invalid team credentials, or this account lacks access.'); return; }
      await load();
    } finally {
      setLoggingIn(false);
    }
  };

  const pendingCount = useMemo(() => requests.filter(r => r.status === 'PENDING').length, [requests]);
  const grantOf = (r: PreviewRequestView): string[] =>
    sel[r.id] ?? (r.approvedApps.length ? r.approvedApps : r.requestedApps);
  const toggle = (r: PreviewRequestView, appId: string) => {
    const cur = grantOf(r);
    setSel(prev => ({ ...prev, [r.id]: cur.includes(appId) ? cur.filter(a => a !== appId) : [...cur, appId] }));
  };
  const guard = async (fn: () => Promise<any>) => {
    try { await fn(); await load(); }
    catch (e) { if (e instanceof TeamAuthError) setAuthed(false); else setError('Action failed.'); }
  };
  const approve = (r: PreviewRequestView) => guard(() => decideRequest(r.id, grantOf(r)));
  const reject = (r: PreviewRequestView) => guard(() => decideRequest(r.id, []));
  const remove = (r: PreviewRequestView) => guard(() => deleteRequest(r.id));

  // ── Login gate ────────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <LandingShell>
        <Narrow>
          <Panel style={{ marginTop: 24 }}>
            <Eyebrow>Team console</Eyebrow>
            <H2>Sign in to review requests</H2>
            <Sub>Use an admin account (e.g. a super-admin) to view and approve preview requests.</Sub>
            <form onSubmit={onLogin} noValidate style={{ marginTop: 18 }}>
              <Field><span className="lbl">Admin email</span>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@example.com" /></Field>
              <div style={{ marginTop: 14 }}><Field><span className="lbl">Password</span>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" /></Field></div>
              {loginError && <div style={{ marginTop: 14 }}><ErrorText role="alert">{loginError}</ErrorText></div>}
              <div style={{ marginTop: 16 }}>
                <PrimaryButton type="submit" disabled={loggingIn} style={{ width: '100%' }}>
                  {loggingIn ? 'Signing in…' : 'Sign in as team'}
                </PrimaryButton>
              </div>
            </form>
          </Panel>
        </Narrow>
      </LandingShell>
    );
  }

  // ── Console ───────────────────────────────────────────────────────────────────
  return (
    <LandingShell>
      <Container>
        <Head>
          <div>
            <Eyebrow>Team console</Eyebrow>
            <H2>Preview access requests</H2>
            <Sub>{requests.length} total · {pendingCount} pending. Approve the apps each requester may preview.</Sub>
          </div>
          <TopActions>
            <GhostButton type="button" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</GhostButton>
            <GhostButton type="button" onClick={() => { teamLogout(); setAuthed(false); }}>Sign out</GhostButton>
          </TopActions>
        </Head>

        {error && <div style={{ marginBottom: 16 }}><ErrorText>{error}</ErrorText></div>}

        {requests.length === 0 ? (
          <Empty>{loading ? 'Loading…' : 'No requests yet. Submissions from the signup wizard appear here.'}</Empty>
        ) : (
          <List>
            {requests.map(r => {
              const grant = grantOf(r);
              return (
                <Row key={r.id}>
                  <RowHead>
                    <div>
                      <Who>{r.name || r.email}</Who>
                      <Meta>{r.company || 'Individual'}{r.industry ? ` · ${r.industry}` : ''}{r.orgSize ? ` · ${r.orgSize} people` : ''}</Meta>
                    </div>
                    <StatusBadge $tone={r.status}>{r.status}</StatusBadge>
                  </RowHead>

                  <Details>
                    <D><DK>Email</DK><DV>{r.email}{' '}
                      {r.emailVerified
                        ? <VerifiedTag><FiCheck /> verified</VerifiedTag>
                        : <UnverifiedTag>unverified</UnverifiedTag>}</DV></D>
                    <D><DK>Phone</DK><DV>{r.phone || '—'}</DV></D>
                    <D><DK>Company</DK><DV>{r.company || '—'}</DV></D>
                    <D><DK>Role</DK><DV>{r.role || '—'}</DV></D>
                    <D><DK>Industry</DK><DV>{r.industry || '—'}</DV></D>
                    <D><DK>Org size</DK><DV>{r.orgSize ? `${r.orgSize} people` : '—'}</DV></D>
                    <D><DK>Requested</DK><DV>{r.requestedApps.map(appName).join(', ') || '—'}</DV></D>
                    <D><DK>Submitted</DK><DV>{fmt(r.createdAt)}</DV></D>
                    {r.decidedAt && <D><DK>Decided</DK><DV>{fmt(r.decidedAt)}</DV></D>}
                  </Details>

                  {r.useCase && <UseCase>“{r.useCase}”</UseCase>}

                  <GrantLabel>Grant access to:</GrantLabel>
                  <Apps>
                    {DEPLOYED_APPS.map(app => {
                      const requested = r.requestedApps.includes(app.id);
                      const on = grant.includes(app.id);
                      return (
                        <AppToggle key={app.id} type="button" $on={on} $accent={app.accent}
                          onClick={() => toggle(r, app.id)} title={requested ? 'Requested' : 'Not requested'}>
                          <span>{on ? <FiCheck /> : <FiX />}</span>
                          {app.name}{!requested && <NotReq>not requested</NotReq>}
                        </AppToggle>
                      );
                    })}
                  </Apps>

                  {r.status !== 'PENDING' && (
                    <Decided>
                      {r.status === 'APPROVED'
                        ? <>Approved: {r.approvedApps.map(appName).join(', ') || '—'}</>
                        : <>Rejected</>}
                    </Decided>
                  )}

                  <Actions>
                    <PrimaryButton type="button" onClick={() => approve(r)} disabled={grant.length === 0}>
                      <FiCheck /> Approve {grant.length} app{grant.length === 1 ? '' : 's'}
                    </PrimaryButton>
                    <GhostButton type="button" onClick={() => reject(r)}><FiX /> Reject</GhostButton>
                    <IconBtn type="button" onClick={() => remove(r)} title="Delete request"><FiTrash /></IconBtn>
                  </Actions>
                </Row>
              );
            })}
          </List>
        )}
      </Container>
    </LandingShell>
  );
};

export default TeamRequests;

// ── styled ────────────────────────────────────────────────────────────────────

const Head = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin: 16px 0 20px;`;
const TopActions = styled.div`display: flex; gap: 8px;`;
const Empty = styled.div`
  padding: 44px; text-align: center; color: ${T.text2}; font-weight: 600;
  background: ${T.surface}; border: 1px dashed #cbd5d3; border-radius: 18px;
`;
const List = styled.div`display: flex; flex-direction: column; gap: 14px; padding-bottom: 8px;`;
const Row = styled.div`
  background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 18px; padding: 20px;
  box-shadow: 0 4px 14px rgba(13,52,47,.05);
`;
const RowHead = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;`;
const Who = styled.div`font-size: 16px; font-weight: 800; color: ${T.text};`;
const Meta = styled.div`font-size: 12.5px; color: ${T.muted}; margin-top: 2px;`;
const UseCase = styled.p`font-size: 13.5px; color: ${T.text2}; font-style: italic; margin: 12px 0 0;`;
const Details = styled.div`display:grid; grid-template-columns:repeat(2,1fr); gap:0 20px; margin-top:14px;
  @media (max-width:520px){ grid-template-columns:1fr; }`;
const D = styled.div`display:grid; grid-template-columns:88px 1fr; gap:8px; padding:6px 0; border-bottom:1px solid ${T.field}; font-size:13px; align-items:center;`;
const DK = styled.span`color:${T.muted}; font-weight:700; font-size:11px; text-transform:uppercase; letter-spacing:.03em;`;
const DV = styled.span`color:${T.text}; word-break:break-word;`;
const VerifiedTag = styled.span`display:inline-flex; align-items:center; gap:3px; font-size:10.5px; font-weight:700; color:#1B7A52;
  background:#E3F3EC; border-radius:20px; padding:1px 8px; svg{font-size:11px;}`;
const UnverifiedTag = styled.span`font-size:10.5px; font-weight:700; color:#9A6510; background:${T.warnBg}; border-radius:20px; padding:1px 8px;`;
const GrantLabel = styled.div`font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: ${T.muted}; margin: 16px 0 8px;`;
const Apps = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
interface ToggleProps { $on: boolean; $accent: string; }
const AppToggle = styled.button<ToggleProps>`
  display: inline-flex; align-items: center; gap: 7px; cursor: pointer; font-family: inherit;
  font-size: 13px; font-weight: 700; padding: 8px 12px; border-radius: 20px;
  color: ${p => p.$on ? '#fff' : T.text2};
  background: ${p => p.$on ? p.$accent : T.field};
  border: 1.5px solid ${p => p.$on ? p.$accent : T.fieldBorder};
  span { display: inline-flex; font-size: 13px; }
`;
const NotReq = styled.em`font-style: normal; font-weight: 500; font-size: 11px; opacity: .8;`;
const Decided = styled.div`font-size: 12.5px; font-weight: 600; color: ${T.text2}; margin-top: 12px;`;
const Actions = styled.div`display: flex; align-items: center; gap: 10px; margin-top: 16px; flex-wrap: wrap;`;
const IconBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 42px; height: 42px; cursor: pointer; border-radius: 12px;
  color: ${T.muted}; background: ${T.field}; border: 1px solid ${T.border}; font-size: 16px;
  &:hover { color: ${T.danger}; }
`;
